/* eslint-disable no-unused-vars */
/* eslint-disable no-console */
import React from 'react';
import { Button, Grid, TextField, Typography } from '@material-ui/core';
import ActionsButtonsBaseModal from 'components/atoms/ActionsButtonsBaseModal';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import DayjsUtils from '@date-io/dayjs';
import ptBR from 'dayjs/locale/pt-br';
import dayjs from 'dayjs';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { postUnpauseDailyBulletin } from 'services/daily-bulletin';
import ButtonWithLoading from 'components/atoms/ButtonWithLoading';
import { IModalUnpauseProps } from './types';
import useStyles from './style';

const FORM_ID = 'UNPAUSE_FORM_MODAL';
const DEFAULT_HELPER_TEXT = 'Obrigatório';
const DEFAULT_OK_TEXT = 'Obrigatório';
const DEFAULT_ERROR_TEXT = 'Preenchimento inválido.';

const MAX_DATE = dayjs().format('YYYY-MM-DD');

const ModalUnpause = ({
  children,
  open,
  onClose,
  gridRow,
  reloadData,
}: IModalUnpauseProps) => {
  const classes = useStyles();
  const [loadingSaveForm, setLoadingSaveForm] = React.useState<boolean>(false);

  const validationSchema = Yup.object({
    pauseEnd: Yup.mixed()
      .required(DEFAULT_OK_TEXT)
      .test('is-greater-than', DEFAULT_ERROR_TEXT, value => {
        const minDate = dayjs(gridRow?.pauseStart);
        const test = dayjs(value);

        const validation =
          // testa a data e verifica a validação de data mínima,
          // o segundo parâmetro utilizado é a granularidade da comparação
          test.isValid() &&
          (test.isSame(minDate, 'day') || test.isAfter(minDate, 'day')) &&
          // testa a data e verifica a validação de data máxima,
          // o segundo parâmetro utilizado é a granularidade da comparação
          (test.isSame(MAX_DATE, 'day') || test.isBefore(MAX_DATE, 'day'));

        return validation;
      }),
  });

  const formik = useFormik({
    initialValues: {
      activity: '',
      equipment: '',
      pauseStart: '',
      pauseEnd: null,
    },
    validationSchema,
    onSubmit: formValues => {
      const { pauseEnd } = formValues;

      const params = {
        id: gridRow?.activityRealId,
        completionOfPause: dayjs(pauseEnd).utc().format('YYYY-MM-DDTHH:mm:ssZ'),
      };
      // Se for enviado com sucesso
      setLoadingSaveForm(true);
      postUnpauseDailyBulletin(params)
        .then(() => {
          // solicita uma recarga de dados
          if (reloadData) reloadData();
          // e fecha o formulário.
          onClose();
        })
        .finally(() => setLoadingSaveForm(false));
    },
  });

  const handleDateChange = React.useCallback(
    (name, value) => {
      formik.setFieldTouched(name);
      formik.setFieldValue(name, value);
    },
    [formik],
  );

  React.useEffect(() => {
    if (formik.dirty && !open) {
      formik.resetForm();
    }
  }, [formik, open]);

  const extraPropsPauseEnd = React.useMemo(() => {
    if (!formik.values.pauseEnd) {
      return { helperText: 'Obrigatório' };
    }
    return {};
  }, [formik.values.pauseEnd]);

  const renderBody = React.useCallback(() => {
    return (
      <form id={FORM_ID} onSubmit={formik.handleSubmit}>
        <Grid container className={classes.bodyContent} spacing={0}>
          <Grid>
            <Typography className={classes.formMessage}>
              Preencha os dados obrigatórios para retomar a atividade:
            </Typography>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Atividade"
                variant="outlined"
                value={gridRow?.activity}
                disabled
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Equipamento"
                variant="outlined"
                value={gridRow?.equipment}
                disabled
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                id="pauseStart"
                name="pauseStart"
                label="Início da pausa"
                variant="outlined"
                disabled
                value={dayjs(gridRow?.pauseStartBr).format('DD/MM/YY')}
              />
            </Grid>
            <Grid item xs={6}>
              <KeyboardDatePicker
                name="pauseEnd"
                value={formik.values.pauseEnd}
                onChange={date => handleDateChange('pauseEnd', date)}
                inputVariant="outlined"
                label="Término da pausa"
                minDate={dayjs(gridRow?.pauseStartBr)}
                maxDate={MAX_DATE}
                minDateMessage="Preenchimento inválido."
                invalidDateMessage="Preenchimento inválido."
                format="DD/MM/YYYY"
                clearable
                clearLabel="Limpar"
                cancelLabel="Cancelar"
                helperText={
                  (formik.touched.pauseEnd && formik.errors.pauseEnd) ||
                  DEFAULT_HELPER_TEXT
                }
                {...extraPropsPauseEnd}
              />
            </Grid>
          </Grid>
        </Grid>
      </form>
    );
  }, [
    classes.bodyContent,
    classes.formMessage,
    extraPropsPauseEnd,
    formik.errors.pauseEnd,
    formik.handleSubmit,
    formik.touched.pauseEnd,
    formik.values.pauseEnd,
    gridRow?.activity,
    gridRow?.equipment,
    gridRow?.pauseStartBr,
    handleDateChange,
  ]);

  const renderFooter = React.useCallback(() => {
    return (
      <div className={classes.footer}>
        <Button color="primary" onClick={onClose}>
          Cancelar
        </Button>
        <ButtonWithLoading
          form={FORM_ID}
          disabled={!formik.dirty || !formik.isValid}
          type="submit"
          color="primary"
          isLoading={loadingSaveForm}
        >
          Salvar
        </ButtonWithLoading>
      </div>
    );
  }, [classes.footer, formik.dirty, formik.isValid, loadingSaveForm, onClose]);

  return (
    <>
      <MuiPickersUtilsProvider locale={ptBR} utils={DayjsUtils}>
        <ActionsButtonsBaseModal
          title="Retomar"
          body={renderBody()}
          footer={renderFooter()}
          open={open}
          onClose={onClose}
          width="629px"
        >
          {children}
        </ActionsButtonsBaseModal>
      </MuiPickersUtilsProvider>
    </>
  );
};

export default ModalUnpause;
